import { useMemo } from "react";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ENTIDADES, municipiosDe } from "@/lib/mexico";

/**
 * Cascading Entidad → Municipio dropdowns.
 * Renders entidad first (as required by SEP), then a dependent municipio select.
 * When entidad changes and current municipio no longer belongs, it clears itself
 * via the onChange callback.
 *
 * Props:
 *   entidad, municipio: current values (strings)
 *   onChange({entidad, municipio}): called with new values
 *   idPrefix: optional prefix for data-testid attributes
 *   entidadLabel / municipioLabel: custom labels
 *   required: whether to mark asterisk
 *   showMunicipio: bool (default true) — set false to hide municipio select
 */
export function EntidadMunicipioSelect({
  entidad = "",
  municipio = "",
  onChange,
  idPrefix = "",
  entidadLabel = "Entidad federativa",
  municipioLabel = "Municipio / Alcaldía",
  required = false,
  showMunicipio = true,
}) {
  const municipios = useMemo(() => municipiosDe(entidad), [entidad]);

  const setEntidad = (v) => {
    const value = v === "__none__" ? "" : v;
    // If current municipio no longer belongs to this entidad, clear it.
    const stillValid = municipiosDe(value).includes(municipio);
    onChange({ entidad: value, municipio: stillValid ? municipio : "" });
  };
  const setMunicipio = (v) => {
    const value = v === "__none__" ? "" : v;
    onChange({ entidad, municipio: value });
  };

  const testEnt = idPrefix ? `${idPrefix}-entidad` : "sel-entidad";
  const testMun = idPrefix ? `${idPrefix}-municipio` : "sel-municipio";

  return (
    <>
      <div className="space-y-1.5">
        <Label className="text-xs">
          {entidadLabel}
          {required && <span className="text-[#9F2241] ml-1">*</span>}
        </Label>
        <Select value={entidad || "__none__"} onValueChange={setEntidad}>
          <SelectTrigger data-testid={testEnt}>
            <SelectValue placeholder="Seleccione..." />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="__none__">— Sin seleccionar —</SelectItem>
            {ENTIDADES.map((e) => (
              <SelectItem key={e} value={e}>{e}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {showMunicipio && (
        <div className="space-y-1.5">
          <Label className="text-xs">
            {municipioLabel}
            {required && <span className="text-[#9F2241] ml-1">*</span>}
          </Label>
          <Select
            value={municipio || "__none__"}
            onValueChange={setMunicipio}
            disabled={!entidad}
          >
            <SelectTrigger data-testid={testMun}>
              <SelectValue placeholder={entidad ? "Seleccione..." : "Primero elija entidad"} />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="__none__">— Sin seleccionar —</SelectItem>
              {municipios.map((m) => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </>
  );
}
